import type { ObjectBundle } from '@/entities/metadata/api/useScreenBundle';
import type { SystemObject, SystemObjectField } from '@/shared/types/system';

/** Kullanıcıya gösterilecek tek bir değişiklik. */
export interface MetadataChangeItem {
  kind: 'field-added' | 'field-removed' | 'field-changed' | 'object-changed';
  /** Alan değişikliklerinde alanın adı (grid kolonu ile eşleşir), nesne değişikliğinde null. */
  fieldName: string | null;
  /** Çeviri anahtarı üretmek için değil, doğrudan gösterim için hazır etiket. */
  label: string;
  /** Nesne/alan özelliği değiştiyse hangi özellik. */
  property?: string;
}

export interface BundleDiff {
  items: MetadataChangeItem[];
  /** Grid'de vurgulanacak kolon adları (eklenen + değişen). */
  highlightFields: string[];
}

/**
 * Kullanıcının fark edeceği alan özellikleri.
 *
 * Tamamı değil bilinçli bir alt küme: `UpdateDate` gibi teknik alanlar her kayıtta
 * değişir ve "şu alan değişti" uyarısını gürültüye boğardı.
 */
const FIELD_PROPS: Array<{ key: keyof SystemObjectField; label: string }> = [
  { key: 'fieldCaption', label: 'Başlık' },
  { key: 'visible', label: 'Görünürlük' },
  { key: 'readOnly', label: 'Salt okunur' },
  { key: 'required', label: 'Zorunlu' },
  { key: 'editorTypeId', label: 'Editör tipi' },
  { key: 'fieldDbTypeId', label: 'Alan tipi' },
  { key: 'displayFormat', label: 'Biçim' },
  { key: 'displayOrder', label: 'Sıra' },
  { key: 'width', label: 'Genişlik' },
  { key: 'defaultValue', label: 'Varsayılan değer' },
  { key: 'lookupDataSourceObjectId', label: 'Lookup kaynağı' },
  { key: 'calculatedExpression', label: 'Hesaplama' },
];

const OBJECT_PROPS: Array<{ key: keyof SystemObject; label: string }> = [
  { key: 'caption', label: 'Başlık' },
  { key: 'readOnly', label: 'Salt okunur' },
  { key: 'isReadOnly', label: 'Salt okunur' },
  { key: 'pagedLoading', label: 'Kademeli yükleme' },
  { key: 'maxRecords', label: 'Kayıt sınırı' },
  { key: 'whereClause', label: 'Filtre koşulu' },
  { key: 'orderClause', label: 'Sıralama' },
  { key: 'editModeId', label: 'Düzenleme kipi' },
  { key: 'calendarEnabled', label: 'Takvim görünümü' },
];

function byId(fields: SystemObjectField[]): Map<string, SystemObjectField> {
  const map = new Map<string, SystemObjectField>();
  for (const f of fields) map.set(String(f.objectFieldId).toLowerCase(), f);
  return map;
}

function nameOf(field: SystemObjectField): string {
  return field.fieldCaption?.trim() || field.fieldName || '';
}

/**
 * İki bundle arasındaki, kullanıcının gözle göreceği farkı çıkarır.
 *
 * Sunucudan gelen `MetadataChanged` sinyali yalnızca nesne kimliğini taşıyor — neyin
 * değiştiğini söylemiyor. Bu yüzden fark istemcide, elde zaten duran eski bundle ile
 * yenisi karşılaştırılarak bulunuyor: fazladan istek yok.
 */
export function diffObjectBundle(previous: ObjectBundle | undefined, next: ObjectBundle | undefined): BundleDiff {
  if (!previous || !next) return { items: [], highlightFields: [] };

  const items: MetadataChangeItem[] = [];
  const highlight: string[] = [];

  const before = byId(previous.fields ?? []);
  const after = byId(next.fields ?? []);

  for (const [id, field] of after) {
    const old = before.get(id);
    if (!old) {
      items.push({ kind: 'field-added', fieldName: field.fieldName, label: `${nameOf(field)} alanı eklendi` });
      if (field.fieldName) highlight.push(field.fieldName);
      continue;
    }
    for (const prop of FIELD_PROPS) {
      if (old[prop.key] !== field[prop.key]) {
        items.push({
          kind: 'field-changed',
          fieldName: field.fieldName,
          label: `${nameOf(field)}: ${prop.label} değişti`,
          property: String(prop.key),
        });
        if (field.fieldName) highlight.push(field.fieldName);
        break; // alan başına tek satır yeter; hepsini saymak listeyi şişirir
      }
    }
  }

  for (const [id, field] of before) {
    if (!after.has(id)) {
      items.push({ kind: 'field-removed', fieldName: field.fieldName, label: `${nameOf(field)} alanı kaldırıldı` });
    }
  }

  // `readOnly`/`isReadOnly` gibi eş anlamlı iki alan aynı anda değişiyor; etikete göre
  // tekilleştirilmezse kullanıcı aynı cümleyi iki kez görürdü.
  const seen = new Set<string>();
  for (const prop of OBJECT_PROPS) {
    if (previous.object?.[prop.key] === next.object?.[prop.key] || seen.has(prop.label)) continue;
    seen.add(prop.label);
    items.push({
      kind: 'object-changed',
      fieldName: null,
      label: `Ekran: ${prop.label} değişti`,
      property: String(prop.key),
    });
  }

  return { items, highlightFields: [...new Set(highlight)] };
}

/** Şeritte gösterilecek tek satırlık özet. */
export function summarizeDiff(items: MetadataChangeItem[]): string {
  if (items.length === 0) return 'Ekran tasarımı güncellendi';
  if (items.length === 1) return items[0].label;
  return `${items[0].label} +${items.length - 1} değişiklik daha`;
}
